import {
  InformationCircleIcon,
  ChartBarIcon,
  PlusCircleIcon,
  ViewGridAddIcon,
  ViewGridIcon,
  PuzzleIcon,
  RefreshIcon,
  UserGroupIcon,
  StarIcon,
  UserCircleIcon,
} from '@heroicons/react/outline'
import animateScrollTo from 'animated-scroll-to'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useInitialData } from './contexts/InitialDataContext'
import { Alert } from './components/alerts/Alert'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { GameTypeIndicator } from './components/game/GameTypeIndicator'
import { AboutModal } from './components/modals/AboutModal'
import { BantumiPromoModal } from './components/modals/BantumiPromoModal'
import { InfoModal } from './components/modals/InfoModal'
import { DevModal } from './components/modals/DevModal'
import { StatsModal } from './components/modals/StatsModal'
import { NewGameModal } from './components/modals/NewGameModal'
import { RoomModal } from './components/modals/RoomModal'
import { RoomInviteConfirmModal } from './components/modals/RoomInviteConfirmModal'
import { ChampionshipModal } from './components/modals/ChampionshipModal'
import { NewFeaturesModal } from './components/modals/NewFeaturesModal'
import { FairPlayModal } from './components/modals/FairPlayModal'
import { DailyGamePromptModal } from './components/modals/DailyGamePromptModal'
import { ProfileModal } from './components/modals/ProfileModal'
import { DifficultyList } from './components/lists/DifficultyList'
import { Icon } from './components/icon/icon'
import { HeaderMenu } from './components/menu/HeaderMenu'
import { BuyMeACoffeeButton } from './components/buttons/BuyMeACoffeeButton'
import { initBreakAds, noteRoundEnded, showBreakAd } from './lib/breakAd'
import {
  getGlobalStatsFromAPI,
  getStateFromAPI,
  getStaticWords,
} from './lib/api-v2'
import {
  queueStateSync,
  syncStateNow,
  initBeforeUnloadHandler,
  clearSyncTimeout,
} from './lib/stateSync'
import { WIN_MESSAGES } from './constants/strings'
import { addStatsForCompletedGame } from './lib/stats'
import {
  gameKey,
  getSize,
  truncate,
  wordKey,
  createId,
} from './lib/localStorage'
import { CharValue, Word } from './lib/statuses'
import { MAX_NUMBER_OF_GUESSES } from './constants/constants'
import { ThemeToggle } from './components/theme/ThemeToggle'
import { ThemeContext } from './components/theme/ThemeContext'
import { CreatePuzzleModal } from './components/modals/CreatePuzzleModal'
import { SyncIndicator } from './components/sync/SyncIndicator'
import { times, random as rand, isNil, map } from 'lodash'
import {
  addGTM,
  GameType,
  getDebugInfo,
  getGridMaxWidthClassName,
  getGuessLength,
  getInitialCurentGuess,
  isLocalhost,
  removeLetter,
  setLetter,
} from './lib/utils'
import { ModalId, ModalType } from './components/modals/BaseModal'
import { getAllWords } from './constants/wordlist'
import {
  isWordInWordList,
  isWinningWord,
  isWordEqual,
  getCurrentWord,
  getRandomWord,
  getTodayDayIndex,
} from './lib/words'
import { usePersistedReducer } from './hooks/usePersistedReducer'
import {
  gameReducer,
  initialState as gameInitialState,
  State as GameState,
  Action as GameAction,
  View,
  Difficulty,
  getInitialState,
} from './hooks/gameReducer'
import {
  wordReducer,
  initialState as wordInitialState,
  State as WordState,
  Action as WordAction,
} from './hooks/wordReducer'
import { ThemeValue } from './lib/theme'
import { shouldCountForRankings, isCurrentDailyGame } from './lib/gameUtils'
import PKG from '../package.json'

import './lib/firebase'
import { updateChampionshipScore, calculateGameScore } from './lib/championship'
import {
  getUserRooms,
  updateRoomRanking,
  joinRoomByCode,
  getRoomByInviteCode,
  Room,
} from './lib/rooms'
import {
  hasSeenNewFeatures,
  markNewFeaturesSeen,
  hasSeenBantumiPromo,
  markBantumiPromoSeen,
  hasCompletedDay,
  markDayAsCompleted,
  updateUserActivity,
} from './lib/userProfile'
import { useVersionCheck } from './hooks/useVersionCheck'
import {
  initGoogleAuth,
  onAuthChange,
  getCurrentUserId,
  waitForGoogleUserId,
} from './lib/googleAuth'
import { adoptUserId, readStoredUserId } from './lib/sessionUser'

const ALERT_TIME_MS = 2000
const NEW_MODAL_TIME_MS = 500

function App() {
  const { state, dispatch } = usePersistedReducer<GameState, GameAction>(
    gameReducer,
    gameInitialState,
    gameKey
  )

  const { difficulty, theme, view, game, stats } = state

  const { state: wordsState, dispatch: dispatchWord } = usePersistedReducer<
    WordState,
    WordAction
  >(wordReducer, wordInitialState, wordKey, false)
  const context = React.useContext(ThemeContext)

  const {
    day,
    random,
    solution: loadedSolution,
    guesses = [],
    currentGuess = getInitialCurentGuess(difficulty),
  } = game?.[difficulty] ?? getInitialState(difficulty)

  const [globalStats, setGlobalStats] = useState()
  const [isGameWon, setIsGameWon] = useState<Record<number, boolean>>({})
  const [isGameLost, setIsGameLost] = useState<Record<number, boolean>>({})
  const [isModalOpen, setIsModalOpenState] = useState<ModalType>(false)
  const [isModalOpenRegistered, setIsModalOpenRegistered] =
    useState<ModalType>(false)
  const [isNotEnoughLetters, setIsNotEnoughLetters] = useState(false)
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false)
  const [shareComplete, setShareComplete] = useState<
    'status' | 'screenshot' | false
  >(false)
  const [shareFailed, setShareFailed] = useState(false)
  const [successAlert, setSuccessAlert] = useState('')
  const [userInteracted, setUserInteracted] = useState(false)
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 })
  // Initialize userId - prefer Google userId if signed in, otherwise use local
  const [userId] = useState(() => {
    const googleUserId = getCurrentUserId()
    return googleUserId || state.id || createId()
  })
  const [showFairPlayModal, setShowFairPlayModal] = useState(false)
  const [pendingRankingUpdate, setPendingRankingUpdate] = useState<{
    score: number
    isWin: boolean
    difficulty: Difficulty
  } | null>(null)
  const [newFeaturesSeen, setNewFeaturesSeen] = useState(true) // Default true to hide modal until loaded
  const [bantumiPromoSeen, setBantumiPromoSeen] = useState(true) // Default true to hide until loaded
  const [pendingRoomInvite, setPendingRoomInvite] = useState<{
    code: string
    room: Room
  } | null>(null)
  const [showDailyPrompt, setShowDailyPrompt] = useState(false)
  const [dailyPromptDismissed, setDailyPromptDismissed] = useState(false)
  const [todayCompleted, setTodayCompleted] = useState(false)
  const [isLoadingState, _setIsLoadingState] = useState(true) // Loading Firebase state
  const isLoadingStateRef = useRef(true) // Ref to track loading without triggering re-renders
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const gameJustEndedRef = useRef(false) // Track if game just ended to trigger immediate sync

  // Wrapper to log state changes
  const setIsLoadingState = (value: boolean) => {
    _setIsLoadingState(value)
    // DON'T automatically update ref here, we control it manually
  }

  // Version check for automatic cache invalidation
  useVersionCheck()

  const gridContainerRef = useRef<HTMLDivElement>(null)

  const currentGuessLength = useMemo(
    () => getGuessLength(currentGuess),
    [currentGuess]
  )

  const [{ solution, solutionCreator }, setSolution] = useState<{
    solution: Word
    solutionCreator?: string
  }>(getCurrentWord(day, difficulty))

  useEffect(() => {
    initBreakAds()
  }, [])

  useEffect(() => {
    const size = getSize()

    if (size > 1024 * 1024) {
      truncate('id')

      window.location.reload()
    }
  }, [])

  // Initialize Google Auth and listen for auth state changes
  useEffect(() => {
    // Check if we just reloaded due to profile switch
    const justReloaded = sessionStorage.getItem('google_auth_reload')
    if (justReloaded) {
      sessionStorage.removeItem('google_auth_reload')
      return
    }

    // Initialize Google Auth listener
    initGoogleAuth()

    // A linked Google account is the profile. A new browser must not
    // keep the anonymous id that was created before auth resolved.
    const unsubscribe = onAuthChange(async (googleUserId) => {
      if (!googleUserId) {
        console.log('Google user signed out or not linked')
        return
      }

      console.log(
        'Google user signed in with linked account, userId:',
        googleUserId
      )
      const activeId = readStoredUserId() || userId
      if (googleUserId !== activeId) {
        adoptUserId(googleUserId)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    setSolution(
      random > -1
        ? getRandomWord(random, difficulty)
        : getCurrentWord(day, difficulty)
    )
  }, [day, difficulty, random, wordsState])

  const maxGuess = useMemo(
    () => MAX_NUMBER_OF_GUESSES[difficulty],
    [difficulty]
  )

  const setIsModalOpen = useCallback((type: ModalType) => {
    type && setIsModalOpenRegistered(type)
    setIsModalOpenState(type)
  }, [])

  useEffect(() => {
    if (theme !== context.theme) {
      context.setTheme(theme)
    }
  }, [context, dispatch, theme])

  useEffect(() => {
    // Don't reset game while Firebase state is still loading
    if (isLoadingStateRef.current) {
      return
    }

    // For DAILY games (random === -1), always check if solution changed (new day)
    if (
      random === -1 &&
      loadedSolution &&
      !isWordEqual(solution, loadedSolution)
    ) {
      dispatch({
        type: 'UPDATE_GAME',
        difficulty,
        solution: solution,
        guesses: [],
        currentGuess: getInitialCurentGuess(difficulty),
      })
      return
    }

    // Don't check win/loss while Firebase state is still loading
    if (isLoadingStateRef.current) {
      return
    }

    // Use loadedSolution (from saved state) if available, otherwise use current solution
    const solutionToCheck = loadedSolution || solution
    const gameWasWon = guesses.some((guess) =>
      isWordEqual(guess, solutionToCheck)
    )
    if (gameWasWon) {
      setIsGameWon((prev) => ({ ...prev, [difficulty]: true }))
    }
    if (guesses.length === maxGuess && !gameWasWon) {
      setIsGameLost((prev) => ({ ...prev, [difficulty]: true }))
    }
  }, [
    difficulty,
    dispatch,
    guesses,
    loadedSolution,
    maxGuess,
    random,
    solution,
  ])

  useEffect(() => {
    let cancelled = false

    waitForGoogleUserId().then((googleUserId) => {
      if (cancelled) return
      const activeId = readStoredUserId() || userId
      if (googleUserId && googleUserId !== activeId) {
        adoptUserId(googleUserId)
        return
      }

      isLocalhost() && console.log('Solution', solution)
      getGlobalStatsFromAPI().then((data) => setGlobalStats(data))

      getStateFromAPI()
        .then((data) => {
          // Always dispatch UPDATE_STATE (even if data is empty) to trigger daily game reset logic
          dispatch({
            type: 'UPDATE_STATE',
            state: data || {},
          })
          // Hide loading screen immediately
          setIsLoadingState(false)

          // Allow state updates to complete before allowing UPDATE_GAME to run
          setTimeout(() => {
            isLoadingStateRef.current = false
          }, 50)
        })
        .catch((error) => {
          console.error('Error loading state:', error)
          // Always hide loading screen, even on error
          // Still dispatch UPDATE_STATE to trigger reset logic for localStorage state
          dispatch({
            type: 'UPDATE_STATE',
            state: {},
          })
          setIsLoadingState(false)
          isLoadingStateRef.current = false
        }) // Handle room invite from URL
      const urlParams = new URLSearchParams(window.location.search)
      const roomCode = urlParams.get('room')
      if (roomCode) {
        // Get room info and show confirmation modal
        getRoomByInviteCode(roomCode.toUpperCase())
          .then((room) => {
            if (room) {
              setPendingRoomInvite({ code: roomCode.toUpperCase(), room })
            }
          })
          .catch((err) => {
            console.error('Error getting room info:', err)
          })
      }

      // Check if user has seen new features modal
      hasSeenNewFeatures(userId).then((seen) => {
        setNewFeaturesSeen(seen)
      })

      // Check if user has seen Bantumi promo modal
      hasSeenBantumiPromo(userId).then((seen) => {
        setBantumiPromoSeen(seen)
      })

      // Load user profile to get display name
      import('./lib/userProfile').then(({ getUserProfile }) => {
        getUserProfile(userId).then((profile) => {
          if (profile?.displayName) {
            setDisplayName(profile.displayName)
          }
        })
      })

      // Update user activity (version tracking)
      updateUserActivity(userId)

      console.log('✅ Initialization useEffect completed')
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check if today's game is already completed (from Firebase)
  useEffect(() => {
    console.log('🔍 Checking if today is completed for difficulty:', difficulty)
    const todayIndex = getTodayDayIndex()
    hasCompletedDay(userId, difficulty, todayIndex).then((completed) => {
      console.log('📅 Today completed:', completed)
      setTodayCompleted(completed)
    })
  }, [userId, difficulty])

  // Check if user is not on today's daily game
  useEffect(() => {
    // Show prompt to go to daily game if:
    // 1. Not on current daily game (on random or future daily instead)
    // 2. Current non-daily game has ended (won or lost)
    // 3. User hasn't dismissed the prompt in this session
    // 4. Today's game is not already completed (checked from Firebase)
    const isOnDailyGame = isCurrentDailyGame(day, random, !!solutionCreator)
    const isNonDailyGameCompleted =
      isGameWon[difficulty] || isGameLost[difficulty]

    if (
      !isOnDailyGame &&
      isNonDailyGameCompleted &&
      !dailyPromptDismissed &&
      !todayCompleted
    ) {
      setShowDailyPrompt(true)
    }
  }, [
    day,
    random,
    solutionCreator,
    difficulty,
    isGameWon,
    isGameLost,
    dailyPromptDismissed,
    todayCompleted,
  ])

  useEffect(() => {
    getStaticWords(difficulty, ({ group, words }) => {
      dispatchWord({
        type: 'UPDATE_STATE',
        state: {
          [group]: words,
        },
      })
    })
  }, [difficulty, dispatchWord])

  useEffect(() => {
    const handleResize = () => {
      if (gridContainerRef.current == null) {
        return
      }
      const gridContainerHeight = gridContainerRef.current.clientHeight
      const gridWidth = Math.min(
        Math.floor(
          gridContainerHeight * (5 / MAX_NUMBER_OF_GUESSES[difficulty])
        ),
        350
      )
      const gridHeight = Math.floor(
        (MAX_NUMBER_OF_GUESSES[difficulty] * gridWidth) / 5
      )
      setGridSize({ width: gridWidth, height: gridHeight })
    }
    window.addEventListener('resize.grid', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize.grid', handleResize)
    }
  }, [difficulty, setGridSize])

  const checkIsModalOpen = useCallback(
    (type: ModalId) => {
      if (typeof isModalOpen === 'string' && isModalOpen === type) {
        return true
      }
      if (Array.isArray(isModalOpen) && isModalOpen?.[0] === type) {
        return true
      }
      return false
    },
    [isModalOpen]
  )
  const checkIsModalCallback = useCallback(() => {
    if (Array.isArray(isModalOpen) && isModalOpen?.[1]) {
      return isModalOpen?.[1]
    }
    return null
  }, [isModalOpen])

  const checkViewPort = () => {
    const currentRow = gridContainerRef.current?.querySelector(
      '.current-row'
    ) as HTMLDivElement | null
    const parent = gridContainerRef.current
    if (parent && currentRow) {
      if (parent.offsetTop > currentRow.offsetTop - parent.scrollTop) {
        animateScrollTo(0)
        animateScrollTo(currentRow.previousElementSibling ?? currentRow, {
          elementToScroll: parent,
        })
      }
      if (
        parent.offsetTop + parent.offsetHeight <
        currentRow.offsetTop + currentRow.offsetHeight - parent.scrollTop
      ) {
        animateScrollTo(0)
        animateScrollTo(currentRow.previousElementSibling ?? currentRow, {
          elementToScroll: parent,
        })
      }
    }
  }

  // Initialize beforeunload handler for unsaved changes warning
  useEffect(() => {
    const cleanup = initBeforeUnloadHandler()
    return () => {
      cleanup()
      clearSyncTimeout()
    }
  }, [])

  // Queue state sync (1 minute interval) when state changes
  useEffect(() => {
    checkViewPort()

    // Don't queue sync if game just ended - we'll do immediate sync instead
    if (!gameJustEndedRef.current && userInteracted && !solutionCreator) {
      queueStateSync(state, (data) => setGlobalStats(data))
    }

    // If game just ended (ref was set), sync immediately with the current (updated) state
    if (gameJustEndedRef.current && userInteracted) {
      gameJustEndedRef.current = false // Reset flag
      // Sync with the current state that now has the winning guess in guesses array
      syncStateNow((data) => setGlobalStats(data), state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, userInteracted])

  useEffect(() => {
    if (!isGameWon[difficulty] && !isGameLost[difficulty]) {
      setSuccessAlert('')
      return
    }

    // State sync is now handled in the state useEffect above
    // This just sets the success message
    setSuccessAlert(
      isGameWon[difficulty]
        ? WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
        : ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameLost, isGameWon])

  // Show stats modal after game ends (but not if fair play modal will be shown)
  const prevGameEndedRef = useRef<{ won: boolean; lost: boolean }>({
    won: false,
    lost: false,
  })

  useEffect(() => {
    const currentWon = isGameWon[difficulty] || false
    const currentLost = isGameLost[difficulty] || false
    const prevWon = prevGameEndedRef.current.won
    const prevLost = prevGameEndedRef.current.lost

    // Update ref
    prevGameEndedRef.current = { won: currentWon, lost: currentLost }

    // Only trigger if game JUST ended (transition from not-ended to ended)
    const gameJustEnded = (currentWon && !prevWon) || (currentLost && !prevLost)

    if (!gameJustEnded) {
      return
    }

    // Don't show stats modal if fair play modal is showing or will be shown
    if (showFairPlayModal || pendingRankingUpdate) {
      return
    }

    const timer = setTimeout(() => {
      // Don't open if new-game modal is already open or being opened
      // Also don't open if stat modal is already open
      if (
        !(['new-game', 'new-game-confirm'] as ModalType[]).includes(
          isModalOpenRegistered
        ) &&
        !checkIsModalOpen('stat')
      ) {
        setIsModalOpen('stat')
      }
    }, ALERT_TIME_MS)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGameLost,
    isGameWon,
    showFairPlayModal,
    pendingRankingUpdate,
    difficulty,
  ])

  const onChar = (value: CharValue) => {
    checkViewPort()
    if (
      currentGuessLength < difficulty &&
      guesses.length < maxGuess &&
      !isGameWon[difficulty]
    ) {
      setUserInteracted(true)

      dispatch({
        type: 'UPDATE_CURRENT_GUESS',
        difficulty,
        currentGuess: setLetter(currentGuess, value, difficulty),
      })
    }
  }

  const onReplace = (value: CharValue, index?: number) => {
    checkViewPort()
    if (
      currentGuessLength - 1 < difficulty &&
      guesses.length < maxGuess &&
      !isGameWon[difficulty]
    ) {
      setUserInteracted(true)

      if (isNil(index)) {
        dispatch({
          type: 'UPDATE_CURRENT_GUESS',
          difficulty,
          currentGuess: removeLetter(currentGuess, value),
        })
      } else {
        dispatch({
          type: 'UPDATE_CURRENT_GUESS',
          difficulty,
          currentGuess: setLetter(currentGuess, value, difficulty, index),
        })
      }
    }
  }

  const onDelete = () => {
    checkViewPort()
    if (currentGuessLength - 1 >= 0 && !isGameWon[difficulty]) {
      setUserInteracted(true)

      dispatch({
        type: 'UPDATE_CURRENT_GUESS',
        difficulty,
        currentGuess: removeLetter(currentGuess),
      })
    }
  }

  const onEnter = () => {
    checkViewPort()
    if (isGameWon[difficulty] || isGameLost[difficulty]) {
      return
    }

    if (currentGuessLength !== difficulty) {
      setIsNotEnoughLetters(true)
      return setTimeout(() => {
        setIsNotEnoughLetters(false)
      }, ALERT_TIME_MS)
    }

    addGTM('event', 'guess', {
      guess: currentGuess.join(''),
      difficulty,
    })
    if (
      !isWordInWordList(currentGuess, difficulty) &&
      !isWordEqual(currentGuess, solution)
    ) {
      setIsWordNotFoundAlertOpen(true)
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false)
      }, ALERT_TIME_MS)
    }

    const winningWord = isWinningWord(currentGuess, day, random, difficulty)
    if (
      currentGuessLength === difficulty &&
      guesses.length < maxGuess &&
      !isGameWon[difficulty]
    ) {
      // Use UPDATE_GAME to atomically update both guesses and currentGuess
      // This ensures the winning guess is saved before any sync happens
      dispatch({
        type: 'UPDATE_GAME',
        difficulty,
        guesses: [...guesses, currentGuess],
        currentGuess: getInitialCurentGuess(difficulty),
      })
      if (winningWord) {
        dispatch({
          type: 'UPDATE_STATS',
          difficulty,
          stats: addStatsForCompletedGame(stats, guesses.length, difficulty),
        })
        addGTM('event', 'win', {
          guess: currentGuess.join(''),
          difficulty,
        })

        // Mark that game just ended to trigger sync with updated state
        gameJustEndedRef.current = true

        // Update championship and room rankings (only for current daily game)
        if (shouldCountForRankings(day, random, !!solutionCreator)) {
          const score = calculateGameScore(guesses.length, maxGuess, true)
          // Show fair play modal before updating rankings
          setPendingRankingUpdate({ score, isWin: true, difficulty })
          setShowFairPlayModal(true)
        }

        noteRoundEnded()
        return setIsGameWon((prev) => ({ ...prev, [difficulty]: true }))
      }

      if (guesses.length === maxGuess - 1) {
        dispatch({
          type: 'UPDATE_STATS',
          difficulty,
          stats: addStatsForCompletedGame(
            stats,
            guesses.length + 1,
            difficulty
          ),
        })
        addGTM('event', 'lost', {
          guesses: guesses.map((guess) => guess.join('')),
          difficulty,
        })

        // Mark that game just ended to trigger sync with updated state
        gameJustEndedRef.current = true

        // Update championship for failed game (only for current daily game)
        if (shouldCountForRankings(day, random, !!solutionCreator)) {
          const todayIndex = getTodayDayIndex()
          // Mark day as completed and update championship
          markDayAsCompleted(userId, difficulty, todayIndex, 0).then(
            (marked) => {
              if (marked) {
                setTodayCompleted(true)
                updateChampionshipScore(userId, 0, false, difficulty).catch(
                  (err) => console.error('Error updating championship:', err)
                )
              }
            }
          )
        }

        noteRoundEnded()
        setIsGameLost((prev) => ({ ...prev, [difficulty]: true }))
      }
    }
  }

  const handleShareCopySuccess = useCallback(() => {
    addGTM('event', 'copy', { status: 'success' })
    setShareComplete('status')
    setTimeout(() => {
      setShareComplete(false)
    }, ALERT_TIME_MS)
  }, [])

  const handleShareScreenshotSuccess = useCallback(() => {
    addGTM('event', 'copy', { status: 'success screenshot' })
    setShareComplete('screenshot')
    setTimeout(() => {
      setShareComplete(false)
    }, ALERT_TIME_MS)
  }, [])

  const handleShareFailure = useCallback(() => {
    addGTM('event', 'copy', { status: 'failed' })
    setShareFailed(true)
    setTimeout(() => {
      setShareFailed(false)
    }, ALERT_TIME_MS)
  }, [])

  // Fair play confirmation handlers
  const handleFairPlayConfirm = useCallback(async () => {
    if (pendingRankingUpdate) {
      const { score, isWin, difficulty: diff } = pendingRankingUpdate
      const todayIndex = getTodayDayIndex()

      // First check if day is already completed (prevent double submission)
      const alreadyCompleted = await hasCompletedDay(userId, diff, todayIndex)
      if (alreadyCompleted) {
        console.warn('Day already completed, cannot submit again')
        setPendingRankingUpdate(null)
        setTodayCompleted(true)
        return
      }

      // Mark day as completed in Firebase (this is atomic, prevents race conditions)
      const marked = await markDayAsCompleted(userId, diff, todayIndex, score)
      if (!marked) {
        console.warn('Failed to mark day as completed')
        setPendingRankingUpdate(null)
        return
      }

      // Update local state
      setTodayCompleted(true)

      // Now update rankings
      updateChampionshipScore(userId, score, isWin, diff).catch((err) =>
        console.error('Error updating championship:', err)
      )

      // Update all user's rooms
      getUserRooms(userId)
        .then((rooms) => {
          rooms.forEach((room) => {
            updateRoomRanking(room.id, userId, score, diff).catch((err) =>
              console.error('Error updating room ranking:', err)
            )
          })
        })
        .catch((err) => console.error('Error getting user rooms:', err))

      setPendingRankingUpdate(null)
    }
  }, [pendingRankingUpdate, userId])

  const handleFairPlayDecline = useCallback(() => {
    // User chose not to add to rankings
    setPendingRankingUpdate(null)
  }, [])

  const handleDifficultyChange = (value: Difficulty) => {
    setSuccessAlert('')
    setIsNotEnoughLetters(false)
    setIsWordNotFoundAlertOpen(false)
    setShareComplete(false)
    setShareFailed(false)
    addGTM('event', 'changeDifficulty', {
      previous: difficulty,
      current: value,
    })
    setUserInteracted(true)
    dispatch({ type: 'SET_DIFFICULTY', difficulty: value })
  }

  const handleNewGame = (type: GameType) => {
    const start = () => {
      addGTM('event', 'newGame', { difficulty })
      setUserInteracted(true)
      setIsGameLost((prev) => ({ ...prev, [difficulty]: false }))
      setIsGameWon((prev) => ({ ...prev, [difficulty]: false }))
      setSuccessAlert('')
      setIsNotEnoughLetters(false)
      setIsWordNotFoundAlertOpen(false)
      setShareComplete(false)
      setShareFailed(false)
      dispatch({
        type: 'UPDATE_GAME',
        difficulty,
        guesses: [],
        currentGuess: getInitialCurentGuess(difficulty),
      })
      if (type === 'in-row') {
        dispatch({ type: 'SET_RANDOM', difficulty, random: -1 })
        dispatch({ type: 'SET_DAY', difficulty, day: day + 1 })
      } else {
        getAllWords(difficulty)
        dispatch({
          type: 'SET_RANDOM',
          difficulty,
          random: rand(0, getAllWords(difficulty).length - 1),
        })
      }
      setIsModalOpen(false)
      setIsModalOpenRegistered(false)
    }

    if (isGameWon[difficulty] || isGameLost[difficulty]) {
      showBreakAd(start)
      return
    }
    start()
  }

  const handleManualEnd = () => {
    setIsModalOpen(false)
    setUserInteracted(true)
    if (!isGameWon[difficulty]) {
      const emptyRow = times(difficulty, () => '-') as Word
      const newCurrentGuess = emptyRow.map(
        (letter, index) => currentGuess[index] ?? letter
      )
      const newGuesses = [...guesses, newCurrentGuess].filter(
        (guess) => guess.length
      )
      for (let i = 0; i < maxGuess; i++) {
        if (!newGuesses[i]) {
          newGuesses[i] = [...emptyRow]
        }
      }

      addGTM('event', 'giveUp', { difficulty, guesses: newGuesses })
      dispatch({ type: 'UPDATE_GUESSES', difficulty, guesses: newGuesses })
      dispatch({
        type: 'UPDATE_STATS',
        difficulty,
        stats: addStatsForCompletedGame(stats, newGuesses.length, difficulty),
      })
      noteRoundEnded()
      setIsGameLost((prev) => ({ ...prev, [difficulty]: true }))
    }
    setIsModalOpenRegistered('new-game')
    setTimeout(() => {
      setSuccessAlert('')
      setIsModalOpen('new-game')
    }, NEW_MODAL_TIME_MS)
  }

  const handleGridIcon = (newView: View) => {
    setUserInteracted(true)
    dispatch({ type: 'SET_VIEW', view: newView })
  }

  const handleTheme = (newTheme: ThemeValue) => {
    setUserInteracted(true)
    dispatch({ type: 'SET_THEME', theme: newTheme })
  }

  const handleModalClose = (newModal?: ModalType) => {
    const fallbackModal = newModal ?? checkIsModalCallback()
    setIsModalOpen(false)
    setIsModalOpenRegistered(false)
    if (fallbackModal) {
      setIsModalOpenRegistered(fallbackModal)
      setTimeout(() => {
        setIsModalOpen(fallbackModal)
      }, NEW_MODAL_TIME_MS)
    }
  }

  const debugInfo = () => {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('debug') === '1'
    ) {
      return (
        <div
          id="debug-info"
          className="bg-white dark:bg-gray-800 max-h-[200px] overflow-auto text-xs dark:text-gray-300"
        >
          {map(getDebugInfo(), (info, key) => (
            <div>
              <b>{key}:</b> {info}
            </div>
          ))}
        </div>
      )
    }

    return ''
  }

  const { isSSR } = useInitialData()

  if (
    !isSSR &&
    (wordsState.all.length === 0 ||
      wordsState.selected.length === 0 ||
      wordsState.random.length === 0 ||
      !solution)
  ) {
    return (
      <div className={context.theme + ' h-[100%] '}>
        <div className="flex justify-center content-center text-center flex-col bg-white dark:bg-gray-800 h-[100%]">
          {debugInfo()}
          <span className="text-xs dark:text-gray-300">{PKG.version}</span>
          <span className="text-5xl mb-5 dark:text-gray-300">Betöltés</span>
          <Icon
            component={RefreshIcon}
            isStandalone
            className="w-40 h-40 animate-spin"
            style={{
              animationDuration: '2s',
              animationDirection: 'reverse',
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={context.theme + ' h-[100%]'}>
      {debugInfo()}
      <Alert message="Nincs elég betű" isOpen={isNotEnoughLetters} />
      <Alert
        message="Nem találtunk ilyen szót"
        isOpen={isWordNotFoundAlertOpen}
      />
      <Alert
        message={`Vesztettél, a megoldás ez volt: ${solution?.join('')}`}
        isOpen={!!isGameLost[difficulty]}
      />
      <Alert
        message={successAlert}
        isOpen={successAlert !== ''}
        variant="success"
      />
      <Alert
        message={`A ${
          shareComplete === 'screenshot' ? 'játék képét ' : 'játékot '
        } kimásoltuk a vágólapra`}
        isOpen={['status', 'screenshot'].includes(shareComplete || '')}
        variant="success"
        className="z-[1200]"
      />
      <Alert
        message="Nem sikerült a megosztás - lehet, hogy beágyazott böngészőt használsz?"
        isOpen={shareFailed}
        variant="warning"
        className="z-[1200]"
      />
      <InfoModal
        isOpen={checkIsModalOpen('info')}
        handleClose={handleModalClose}
        handleModal={setIsModalOpen}
        difficulty={difficulty}
      />
      <DevModal
        isOpen={checkIsModalOpen('dev')}
        solution={solution}
        handleClose={handleModalClose}
      />
      <StatsModal
        isOpen={checkIsModalOpen('stat') || checkIsModalOpen('new-game')}
        handleClose={handleModalClose}
        isMinimal={isModalOpen === false || checkIsModalOpen('new-game')}
        guesses={guesses}
        day={day}
        random={random}
        difficulty={difficulty}
        isGameLost={isGameLost[difficulty]}
        isGameWon={isGameWon[difficulty]}
        solution={isGameWon[difficulty] ? solution : undefined}
        solutionCreator={solutionCreator}
        handleShareCopySuccess={handleShareCopySuccess}
        handleShareScreenshotSuccess={handleShareScreenshotSuccess}
        handleShareFailure={handleShareFailure}
        handleNewGameClick={handleNewGame}
        stats={stats}
        globalStats={globalStats}
        userId={userId}
      />
      <NewGameModal
        isOpen={checkIsModalOpen('new-game-confirm')}
        handleClose={handleModalClose}
        handleFailure={handleManualEnd}
      />
      <AboutModal
        isOpen={checkIsModalOpen('about')}
        handleClose={handleModalClose}
      />
      <BantumiPromoModal
        isOpen={
          checkIsModalOpen('bantumi') ||
          (!bantumiPromoSeen && newFeaturesSeen && !checkIsModalOpen('info'))
        }
        handleClose={() => {
          setBantumiPromoSeen(true)
          markBantumiPromoSeen(userId)
          handleModalClose()
        }}
      />
      <CreatePuzzleModal
        isOpen={checkIsModalOpen('create-puzzle')}
        handleClose={handleModalClose}
        difficulty={difficulty}
      />
      <RoomModal
        isOpen={checkIsModalOpen('rooms')}
        handleClose={handleModalClose}
        userId={userId}
        difficulty={difficulty}
      />
      <ChampionshipModal
        isOpen={checkIsModalOpen('championship')}
        handleClose={handleModalClose}
        userId={userId}
        difficulty={difficulty}
      />
      <NewFeaturesModal
        isOpen={!newFeaturesSeen && !checkIsModalOpen('info')}
        handleClose={() => {
          setNewFeaturesSeen(true)
          markNewFeaturesSeen(userId)
        }}
      />
      <FairPlayModal
        isOpen={showFairPlayModal}
        handleClose={() => {
          setShowFairPlayModal(false)
          // Open stats modal after fair play modal closes
          setTimeout(() => {
            setIsModalOpen('stat')
          }, NEW_MODAL_TIME_MS)
        }}
        onConfirm={handleFairPlayConfirm}
        onDecline={handleFairPlayDecline}
        userId={userId}
      />
      <RoomInviteConfirmModal
        isOpen={pendingRoomInvite !== null}
        roomName={pendingRoomInvite?.room.name || ''}
        onConfirm={() => {
          if (pendingRoomInvite) {
            joinRoomByCode(pendingRoomInvite.code, userId)
              .then((room) => {
                if (room) {
                  setSuccessAlert(`Csatlakoztál a "${room.name}" szobához!`)
                  setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
                }
              })
              .catch((err) => {
                console.error('Error joining room:', err)
              })
            setPendingRoomInvite(null)
            // Remove room parameter from URL
            const newUrl = window.location.pathname
            window.history.replaceState({}, '', newUrl)
          }
        }}
        onCancel={() => {
          setPendingRoomInvite(null)
          // Remove room parameter from URL
          const newUrl = window.location.pathname
          window.history.replaceState({}, '', newUrl)
        }}
      />
      <DailyGamePromptModal
        isOpen={showDailyPrompt}
        onGoToDaily={() => {
          setShowDailyPrompt(false)
          setDailyPromptDismissed(true)
          // Clear all alerts
          setSuccessAlert('')
          setIsNotEnoughLetters(false)
          setIsWordNotFoundAlertOpen(false)
          setShareComplete(false)
          setShareFailed(false)
          // Reset to today's daily game
          dispatch({ type: 'SET_RANDOM', difficulty, random: -1 })
          dispatch({ type: 'SET_DAY', difficulty, day: 0 })
          dispatch({
            type: 'UPDATE_GAME',
            difficulty,
            guesses: [],
            currentGuess: getInitialCurentGuess(difficulty),
          })
        }}
        onStayHere={() => {
          setShowDailyPrompt(false)
          setDailyPromptDismissed(true)
        }}
      />
      <ProfileModal
        isOpen={isProfileModalOpen}
        handleClose={() => setIsProfileModalOpen(false)}
        userId={userId}
        currentDisplayName={displayName}
        onProfileUpdate={(newDisplayName) => {
          setDisplayName(newDisplayName)
        }}
      />
      <div className="bg-white dark:bg-gray-800 transition-all h-[100%]">
        <div
          className="flex flex-col px-2 pt-8 w-[100%] h-[100%] max-w-[500px] mx-auto sm:px-6 lg:px-8"
          style={{ boxSizing: 'border-box' }}
        >
          <div className="flex  mx-1 items-center mb-8 relative z-20">
            <h1 className="text-xl font-bold dark:text-gray-300">
              Szózat<sup>+</sup>
            </h1>
            <SyncIndicator />
            <Icon
              component={InformationCircleIcon}
              onClick={() => {
                setIsModalOpen('info')
              }}
            />
            <DifficultyList
              selected={difficulty}
              onChange={handleDifficultyChange}
            />
            <Icon
              component={UserCircleIcon}
              onClick={() => setIsProfileModalOpen(true)}
              className="h-6 w-6 ml-0"
            />
            <Icon
              component={ThemeToggle}
              onClick={(themeValue) => handleTheme(themeValue)}
              className="h-6 w-6 ml-0"
            />
            <Icon
              component={view === 'full' ? ViewGridIcon : ViewGridAddIcon}
              onClick={() =>
                handleGridIcon(view === 'full' ? 'compact' : 'full')
              }
            />
            <HeaderMenu
              items={[
                {
                  label: 'Statisztika',
                  icon: ChartBarIcon,
                  onClick: () => setIsModalOpen('stat'),
                },
                {
                  label: 'Bajnokság',
                  icon: StarIcon,
                  onClick: () => setIsModalOpen('championship'),
                },
                {
                  label: 'Szobák',
                  icon: UserGroupIcon,
                  onClick: () => setIsModalOpen('rooms'),
                },
                {
                  label: 'Új feladvány',
                  icon: PlusCircleIcon,
                  onClick: () => setIsModalOpen('create-puzzle'),
                },
                {
                  label: 'Új játék',
                  icon: PuzzleIcon,
                  onClick: () =>
                    setIsModalOpen(
                      isGameWon[difficulty] || isGameLost[difficulty]
                        ? 'new-game'
                        : 'new-game-confirm'
                    ),
                },
              ]}
            />
          </div>
          {/* {isLocalhost() && (
            <div className="dark:text-gray-300">
              Nap {day}, Nehézség {difficulty}, Megfejtés {solution}
            </div>
          )} */}
          <GameTypeIndicator
            day={day}
            random={random}
            difficulty={difficulty}
            solutionCreator={solutionCreator}
          />
          <div
            ref={gridContainerRef}
            className={`grow flex justify-center overflow-auto mx-auto w-full mb-5 min-h-[60px] ${getGridMaxWidthClassName(
              difficulty
            )} relative z-10`}
          >
            {isLoadingState && (
              <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-80 z-50">
                <div className="text-center">
                  <RefreshIcon className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-600 dark:text-blue-400" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Állapot betöltése...
                  </p>
                </div>
              </div>
            )}
            <Grid
              guesses={guesses}
              currentGuess={currentGuess}
              day={day}
              random={random}
              size={gridSize}
              full={view === 'full'}
              difficulty={difficulty}
              showCurrentRow={!isGameWon[difficulty]}
            />
          </div>
          <div className="pb-5 keyboard">
            <Keyboard
              onChar={isLoadingState ? () => {} : onChar}
              onDelete={isLoadingState ? () => {} : onDelete}
              onReplace={isLoadingState ? () => {} : onReplace}
              onEnter={isLoadingState ? () => {} : onEnter}
              onDevClicks={{
                D: () => {
                  handleModalClose('dev')
                },
                R: () => window.location.reload(),
              }}
              guesses={guesses}
              currentGuess={currentGuess}
              day={day}
              random={random}
              difficulty={difficulty}
              enabledOnEnter={
                !isLoadingState && currentGuessLength === difficulty
              }
              enabledOnDelete={!isLoadingState && currentGuessLength > 0}
              noDrag={
                isLoadingState ||
                isGameLost[difficulty] ||
                isGameWon[difficulty]
              }
            />
          </div>
          <div className="flex justify-center pb-4 gap-2">
            <BuyMeACoffeeButton username="idavid" />
            <button
              onClick={() => setIsModalOpen('bantumi')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white font-semibold rounded-xl transition-all duration-200 shadow text-sm"
            >
              <img
                src="http://bantumi.hu/manifest/icon-48x48.png"
                alt="Bantumi"
                className="w-5 h-5"
              />{' '}
              Próbáld ki a Bantumit!
            </button>
          </div>
          <div className="flex justify-center pb-6">
            <a
              href="/adatvedelem.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              Adatvédelmi Tájékoztató
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
